import { useEffect, useRef, useState } from "react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AmandaLocalScan from "@/components/AmandaLocalScan";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  User,
  Building2,
  Mail,
  Briefcase,
  Rocket,
  Gauge,
  Sparkles,
} from "lucide-react";
import ExitIntentModal from "@/components/ExitIntentModal";
import { useExitIntent } from "@/hooks/useExitIntent";
import GridActivationBridge from "@/components/GridActivationBridge";
import ReplayGreeting from "@/components/ReplayGreeting";
import AudioDiagnostic from "@/components/AudioDiagnostic";
import { isGridInitialized, speakAs } from "@/lib/audio";

// Lightweight parsers used by the Fast-Track handler to build a Magic Mirror
// URL from whatever the user typed. Same contract as the versions living
// inside GridActivationBridge (kept local to avoid an inter-file dep).
function extractWebsite(input) {
  if (!input) return "";
  const s = String(input).trim();
  const urlMatch = s.match(/([a-z0-9-]+\.[a-z]{2,}(?:\/[^\s]*)?)/i);
  return urlMatch ? urlMatch[1] : "";
}
function extractBusinessName(input) {
  if (!input) return "";
  const s = String(input).trim();
  const parts = s.split(/\s*[—–\-·|]\s*/);
  const first = parts[0].trim();
  return first || s;
}

// Business-Type suggestions surfaced in the Step-01 datalist. Kept as a broad
// write-in list so leaders can type anything (Insurance, Chiropractic, Boat
// Detailing, etc.) but see clean autocomplete for the common verticals.
const INDUSTRY_SUGGESTIONS = [
  "Auto Shop",
  "Auto Repair",
  "Restaurant",
  "Salon",
  "Real Estate",
  "Insurance",
  "Chiropractic",
  "Campground / RV Park",
  "Plumbing",
  "HVAC",
  "Electrician",
  "Roofer",
  "Landscaper",
  "General Contractor",
  "Realtor",
  "Motorcycle Dealer",
  "Other",
];

const STEPS = [
  { id: "contact",      hud: "Step 01 · Contact",       question: "Before we dive in — how can Alfred reach you?" },
  { id: "city",         hud: "Step 02 · Location",      question: "What city is your business in?" },
  { id: "missed_calls", hud: "Step 03 · Lost Signals",  question: "How many calls do you think you miss per week?" },
  { id: "reviews",      hud: "Step 04 · Reputation",    question: "What's your current Google review rating?" },
  { id: "snapshot",     hud: "Step 05 · Grid Snapshot", question: "Which of these describe your business today? (Check all that apply)" },
];

const MISSED_CALLS = ["0-5", "5-10", "10-20", "20+"];
const REVIEWS = ["No reviews", "1-3 stars", "3-4 stars", "4-5 stars"];

// Leak score formula — drama-grade. INDUSTRY_POINTS keys retained for the
// common trades so legacy write-ins ("Auto Shop", "Restaurant", etc.) still
// contribute to the leak calc; anything else falls back to 0.
const CALL_POINTS = { "0-5": 2, "5-10": 5, "10-20": 9, "20+": 14 };
const REVIEW_POINTS = { "No reviews": 14, "1-3 stars": 10, "3-4 stars": 5, "4-5 stars": 1 };
const INDUSTRY_POINTS = { Restaurant: 3, "Auto Shop": 3, "Auto Repair": 3, Salon: 2, "Real Estate": 4, Chiropractic: 3, Other: 2 };

// Grid Readiness signals — deltas applied on top of a 50 baseline.
// Positive lifts, negative drags. Final clamped to 5..95.
const SNAPSHOT_OPTIONS = [
  { id: "has_website",        label: "We have a live website",                              delta: 10,  polarity: "pos" },
  { id: "has_social",         label: "We're active on social media",                        delta: 10,  polarity: "pos" },
  { id: "has_marketing_plan", label: "We have a dedicated marketing plan",                  delta: 15,  polarity: "pos" },
  { id: "small_team",         label: "We have under 10 employees",                          delta: -15, polarity: "neg" },
  { id: "no_followup",        label: "We have no automated follow-up (email/SMS)",          delta: -20, polarity: "neg" },
  { id: "no_mascot",          label: "We have no mascot, character, or brand story",        delta: -15, polarity: "neg" },
  { id: "no_crm",             label: "We have no digital CRM or filing system",             delta: -15, polarity: "neg" },
];

function computeLeak({ industry, missed_calls, reviews }) {
  const c = CALL_POINTS[missed_calls] || 0;
  const r = REVIEW_POINTS[reviews] || 0;
  const i = INDUSTRY_POINTS[industry] || 0;
  const score = Math.min(100, Math.round((c + r + i) * 3));
  const dollars = (c + r + i) * 220; // weekly leak estimate
  return { score, dollars };
}

// Compute the Grid Readiness Score from the Business Snapshot multi-select.
// Baseline 50, apply deltas per selected signal, clamp to 5..95 so the
// gauge never lands at 0% (defeatist) or 100% (would tell them they don't
// need Alfred).
function computeReadiness(snapshot = []) {
  const selected = SNAPSHOT_OPTIONS.filter((o) => snapshot.includes(o.id));
  const delta = selected.reduce((acc, o) => acc + o.delta, 0);
  const raw = 50 + delta;
  const score = Math.max(5, Math.min(95, raw));
  const label =
    score < 35
      ? "Grid Offline — Critical Rebuild Required."
      : score < 55
      ? "Grid Weak — Major Optimization Needed."
      : score < 75
      ? "Grid Stable — High-Leverage Gains Available."
      : "Grid Strong — Ready to Scale Aggressively.";
  const factors = selected.map((o) => ({
    id: o.id,
    label: o.label,
    delta: o.delta,
    polarity: o.polarity,
  }));
  return { score, label, factors };
}

export default function AlfredLocal() {
  return (
    <>
    <AudioDiagnostic />
    <div className="min-h-screen bg-[#060B14] text-white flex flex-col">
      {/* Stand-alone Foyer header — NO nav links to Academy */}
      <header className="border-b border-white/10 bg-[#060B14]/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Logo />
            <span className="hidden sm:inline text-xs hud-label border-l border-white/10 pl-3">
              Local · Concierge Foyer
            </span>
          </div>
          <div className="flex items-center gap-2 text-[10px] hud-label shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-[#34D399] light-pulse glow-green" />
            Architect Online
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        <Concierge />
      </main>
    </div>
    </>
  );
}

function Concierge() {
  return (
    <section className="relative overflow-hidden flex-1" data-testid="local-foyer">
      {/* Cinematic backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(212,175,55,0.10) 0%, transparent 55%), linear-gradient(180deg, #060B14 0%, #0A1122 60%, #060B14 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse at center, transparent 45%, rgba(6,11,20,0.85) 100%)" }}
        />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <AlfredGreeting />
        <div className="mt-8">
          <AmandaLocalScan />
        </div>
        <ConciergeWizard />
      </div>
    </section>
  );
}

function AlfredGreeting() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [hintVisible, setHintVisible] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    setHintVisible(false);
    if (v.paused) v.play().catch(() => {});
  };

  // If the session's audio gate has been passed, auto-fire Alfred's spoken
  // greeting once when this component mounts (belt-and-braces for the case
  // where the video is muted — the SpeechSynthesis line still plays).
  useEffect(() => {
    if (isGridInitialized()) {
      const t = setTimeout(() => speakAs("alfred"), 500);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="text-center" data-testid="alfred-greeting-section">
      <div className="flex items-center gap-3 mb-5 justify-center">
        <div className="hud-line w-10" />
        <span className="hud-label">Live Greeting · Alfred</span>
        <div className="hud-line w-10" />
      </div>

      <div className="flex items-center justify-center mb-4">
        <ReplayGreeting character="alfred" />
      </div>

      <div className="relative mx-auto w-full max-w-2xl">
        <div
          className="absolute -inset-1 rounded-sm pointer-events-none"
          style={{
            background:
              "linear-gradient(135deg, rgba(212,175,55,0.45), rgba(96,165,250,0.2), rgba(212,175,55,0.45))",
            filter: "blur(14px)",
            opacity: 0.55,
          }}
        />
        <div
          className="relative rounded-sm overflow-hidden border border-gold/40"
          style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.5), 0 0 60px rgba(212,175,55,0.15)" }}
        >
          <video
            ref={videoRef}
            src="https://customer-assets.emergentagent.com/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/ce2a19eab1b54186b28beb9441fdceb6_alfred_master_FINAL_v3.mp4"
            autoPlay
            muted
            playsInline
            controls
            data-testid="alfred-greeting-video"
            className="w-full h-auto block bg-black"
            onClick={() => setHintVisible(false)}
          />
          {hintVisible && muted && (
            <button
              onClick={toggleMute}
              data-testid="alfred-greeting-unmute-hint"
              className="absolute top-3 right-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#060B14]/85 backdrop-blur border border-gold/40 text-gold text-xs hover:bg-gold hover:text-[#060B14] transition-colors light-pulse"
            >
              <span aria-hidden="true">🔊</span> Tap to unmute
            </button>
          )}
          <div className="absolute top-3 left-3 z-10 flex items-center gap-2 bg-[#060B14]/85 backdrop-blur px-3 py-1.5 rounded-sm border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F43F5E] light-pulse" />
            <span className="hud-label text-[9px] text-white">Architect · Live</span>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="font-display text-xl sm:text-2xl text-white font-light">
          Meet <span className="text-gold italic">Alfred</span> — Your Service Director
        </div>
      </div>
    </div>
  );
}

function ConciergeWizard() {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({
    contact_name: "",
    business_name: "",
    contact: "", // email OR phone (auto-detected)
    city: "",
    industry: "",
    missed_calls: "",
    reviews: "",
    snapshot: [],
  });
  const [phase, setPhase] = useState("question"); // question | evaluating | result
  const [result, setResult] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [submittingPartial, setSubmittingPartial] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);

  const step = STEPS[stepIdx];

  const setField = (id, val) => {
    setAnswers((a) => ({ ...a, [id]: val }));
  };
  const setAnswer = (val) => setField(step.id, val);

  // Detect whether the "contact" field looks like an email vs phone.
  const contactIsEmail = /@/.test(answers.contact);
  const contactPayload = () => ({
    owner_name: answers.contact_name.trim(),
    business_name: answers.business_name.trim(),
    email: contactIsEmail ? answers.contact.trim() : "",
    phone: contactIsEmail ? "" : answers.contact.trim(),
    industry: answers.industry.trim(),
  });

  // Submit the partial (Step-1) lead as soon as the user leaves the Contact step.
  // This is the "salvage" hook — even if they drop off, John & Parker have
  // their name / business / contact / industry in the CRM tagged 'partial-lead-ebook'.
  const submitPartial = async () => {
    if (leadId || submittingPartial) return null;
    setSubmittingPartial(true);
    try {
      const { data } = await api.post("/local/lead", {
        ...contactPayload(),
        source: "partial-step1",
      });
      setLeadId(data.lead_id);
      return data.lead_id;
    } catch (e) {
      // Non-fatal: user can still complete assessment; we'll retry on final POST.
      console.warn("partial lead capture failed", e?.response?.data || e);
      return null;
    } finally {
      setSubmittingPartial(false);
    }
  };

  const advance = async () => {
    // Fire partial capture on the transition OUT of the Contact step, then
    // pause on the "Choose Your Speed" gate before continuing to Step 02.
    if (step.id === "contact") {
      if (!leadId) await submitPartial();
      setPhase("speed");
      return;
    }
    if (stepIdx + 1 < STEPS.length) {
      setStepIdx(stepIdx + 1);
    } else {
      evaluate();
    }
  };

  // Fast-Track handler — leaves the funnel and jumps into /demo with the
  // pre-filled Magic Mirror params. Uses the same buildDemoUrl-style contract
  // /demo already understands (name, website, industry, optional variant).
  const fastTrackToDemo = () => {
    const nameOnly = extractBusinessName(answers.business_name);
    const site = extractWebsite(answers.business_name) || extractWebsite(answers.contact);
    const params = new URLSearchParams();
    if (nameOnly) params.set("name", nameOnly);
    if (site) params.set("website", site);
    if (answers.industry) params.set("industry", answers.industry.trim());
    // Route campground niches to the campground variant so Bentley's greeting fires.
    const n = String(answers.industry || "").toLowerCase();
    if (n.includes("camp") || n.includes("rv")) params.set("variant", "campground");
    window.location.href = `/demo?${params.toString()}`;
  };

  const continueToFullAudit = () => {
    setPhase("question");
    setStepIdx(1); // step 02 · Location
  };

  const back = () => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  };

  const evaluate = async () => {
    setPhase("evaluating");
    const { score, dollars } = computeLeak(answers);
    const readiness = computeReadiness(answers.snapshot);
    await new Promise((r) => setTimeout(r, 2200));
    try {
      const assessmentPayload = {
        city: answers.city,
        industry: answers.industry,
        missed_calls: answers.missed_calls,
        reviews: answers.reviews,
        leak_score: score,
        leak_dollars: dollars,
        snapshot: answers.snapshot,
        readiness_score: readiness.score,
      };
      let finalLeadId = leadId;
      if (leadId) {
        // Upgrade the partial to a complete lead.
        await api.post(`/local/lead/${leadId}/complete`, assessmentPayload);
      } else {
        // Fallback — partial never fired (rare), POST a fresh full lead.
        const { data } = await api.post("/local/lead", {
          ...contactPayload(),
          ...assessmentPayload,
          source: "assessment",
        });
        finalLeadId = data.lead_id;
        setLeadId(finalLeadId);
      }
      setResult({
        score,
        leak_dollars: dollars,
        lead_id: finalLeadId,
        readiness,
      });
      setPhase("result");
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail[0]?.msg : "Submission failed";
      toast.error(msg || "Submission failed");
      setPhase("question");
    }
  };

  // Arm exit-intent ONLY after the partial capture succeeded AND the user
  // hasn't yet reached the result panel. This is the "bribe" trigger.
  const exitIntentArmed = !!leadId && phase !== "result";
  useExitIntent(() => {
    setShowExitModal(true);
  }, exitIntentArmed);

  if (phase === "evaluating") return <EvaluatingPanel />;
  if (phase === "result") return <ResultPanel result={result} answers={answers} />;
  if (phase === "speed") {
    return (
      <SpeedGate
        businessName={answers.business_name}
        industry={answers.industry}
        submittingPartial={submittingPartial}
        onContinue={continueToFullAudit}
        onFastTrack={fastTrackToDemo}
      />
    );
  }

  const canAdvance =
    (step.id === "contact" &&
      answers.contact_name.trim().length >= 2 &&
      answers.business_name.trim().length >= 2 &&
      answers.contact.trim().length >= 5 &&
      answers.industry.trim().length >= 2) ||
    (step.id === "city" && answers.city.trim().length >= 2) ||
    (step.id === "snapshot") ||
    (step.id !== "contact" && step.id !== "city" && step.id !== "snapshot" && !!answers[step.id]);

  return (
    <div className="mt-12" data-testid="wizard-panel">
      <ProgressBar current={stepIdx} total={STEPS.length} />

      <AlfredBubble label={step.hud}>
        {step.question}
      </AlfredBubble>

      <div className="mt-6" data-testid={`wizard-step-${step.id}`}>
        {step.id === "contact" && (
          <ContactStep
            name={answers.contact_name}
            business={answers.business_name}
            contact={answers.contact}
            industry={answers.industry}
            onName={(v) => setField("contact_name", v)}
            onBusiness={(v) => setField("business_name", v)}
            onContact={(v) => setField("contact", v)}
            onIndustry={(v) => setField("industry", v)}
          />
        )}
        {step.id === "city" && (
          <CityStep value={answers.city} onChange={setAnswer} />
        )}
        {step.id === "missed_calls" && (
          <CardGrid
            cols={4}
            value={answers.missed_calls}
            onChange={setAnswer}
            options={MISSED_CALLS.map((c) => ({ id: c, label: c }))}
            testIdPrefix="missed"
          />
        )}
        {step.id === "reviews" && (
          <CardGrid
            cols={2}
            value={answers.reviews}
            onChange={setAnswer}
            options={REVIEWS.map((c) => ({ id: c, label: c }))}
            testIdPrefix="reviews"
          />
        )}
        {step.id === "snapshot" && (
          <SnapshotStep
            value={answers.snapshot}
            onChange={(next) => setField("snapshot", next)}
          />
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={back}
          disabled={stepIdx === 0}
          data-testid="wizard-back-button"
          className="btn-ghost-gold px-5 py-2.5 rounded-sm text-sm flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <button
          onClick={advance}
          disabled={!canAdvance || submittingPartial}
          data-testid="wizard-next-button"
          className="btn-gold px-6 py-3 rounded-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
        >
          {submittingPartial ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Saving...
            </>
          ) : (
            <>
              {stepIdx === STEPS.length - 1 ? "Evaluate" : "Continue"} <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>

      <ExitIntentModal
        open={showExitModal}
        onClose={() => setShowExitModal(false)}
        contact={answers.contact}
        businessName={answers.business_name}
      />
    </div>
  );
}

function ContactStep({ name, business, contact, industry, onName, onBusiness, onContact, onIndustry }) {
  return (
    <div className="space-y-4" data-testid="contact-step-fields">
      <div>
        <label className="hud-label text-[10px] mb-1.5 flex items-center gap-1.5">
          <User size={11} /> Your Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => onName(e.target.value)}
          placeholder="Jane Wexler"
          data-testid="wizard-contact-name"
          className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold min-h-[48px]"
          maxLength={80}
          autoComplete="name"
        />
      </div>
      <div>
        <label className="hud-label text-[10px] mb-1.5 flex items-center gap-1.5">
          <Building2 size={11} /> Business Name / Website
        </label>
        <input
          type="text"
          value={business}
          onChange={(e) => onBusiness(e.target.value)}
          placeholder="Wexler & Co. — wexlerauto.com"
          data-testid="wizard-contact-business"
          className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold min-h-[48px]"
          maxLength={120}
          autoComplete="organization"
        />
      </div>
      <div>
        <label className="hud-label text-[10px] mb-1.5 flex items-center gap-1.5">
          <Briefcase size={11} /> Business Type / Industry
        </label>
        <input
          type="text"
          value={industry}
          onChange={(e) => onIndustry(e.target.value)}
          placeholder="Auto Shop, Campground, Insurance..."
          list="wizard-industry-suggestions"
          data-testid="wizard-contact-industry"
          className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold min-h-[48px]"
          maxLength={60}
          autoComplete="off"
        />
        <datalist id="wizard-industry-suggestions">
          {INDUSTRY_SUGGESTIONS.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <div className="mt-1.5 text-[10px] text-slate-500 font-mono-aviation">
          Type or pick — Alfred uses this to tailor your Magic Mirror demo.
        </div>
      </div>
      <div>
        <label className="hud-label text-[10px] mb-1.5 flex items-center gap-1.5">
          <Mail size={11} /> Best Email or Phone
        </label>
        <input
          type="text"
          value={contact}
          onChange={(e) => onContact(e.target.value)}
          placeholder="jane@wexlerauto.com  or  (614) 555-0142"
          data-testid="wizard-contact-value"
          className="w-full bg-[#060B14] border border-white/10 rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-gold min-h-[48px]"
          maxLength={80}
          autoComplete="email"
          inputMode="text"
        />
        <div className="mt-1.5 text-[10px] text-slate-500 font-mono-aviation">
          Either works — we&apos;ll route your roadmap to whichever you prefer.
        </div>
      </div>
    </div>
  );
}

// Speed Gate — shown immediately after the Step-01 partial-lead POST.
// Lets the user either finish the full audit for a personalized Readiness
// score OR jump straight to the Magic Mirror demo (bypasses steps 02-05).
function SpeedGate({ businessName, industry, submittingPartial, onContinue, onFastTrack }) {
  return (
    <div className="mt-12 fade-up" data-testid="wizard-speed-gate">
      <div className="flex items-center gap-3 mb-6 justify-center">
        <div className="hud-line w-10" />
        <span className="hud-label text-gold">Choose Your Speed</span>
        <div className="hud-line w-10" />
      </div>

      <div className="text-center max-w-2xl mx-auto mb-8">
        <h2 className="font-display text-2xl sm:text-3xl text-white leading-tight">
          {businessName ? (
            <>
              We&apos;ve locked in <span className="text-gold italic">{businessName}</span>.
            </>
          ) : (
            <>We&apos;ve locked you in.</>
          )}
        </h2>
        <p className="text-slate-400 text-sm mt-3">
          {industry ? (
            <>
              Alfred is prepping a{" "}
              <span className="text-gold">{industry}</span> demo. How would you
              like to proceed?
            </>
          ) : (
            <>Alfred is prepping your foyer. How would you like to proceed?</>
          )}
        </p>
      </div>

      {submittingPartial && (
        <div
          className="text-center text-[11px] text-slate-500 font-mono-aviation mb-4"
          data-testid="speed-gate-saving"
        >
          <Loader2 size={11} className="inline animate-spin mr-1" /> Saving your details...
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Button A — Continue full audit */}
        <button
          type="button"
          onClick={onContinue}
          data-testid="speed-gate-continue"
          className="group text-left rounded-sm border border-gold/40 p-6 bg-[#0A1122] hover:bg-[#0F1830] hover:border-gold transition-all"
          style={{ boxShadow: "0 0 32px -18px rgba(212,175,55,0.6)" }}
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-gold mb-3">
            <Gauge size={12} /> Option A · Recommended
          </div>
          <div className="font-display text-lg sm:text-xl text-white leading-snug mb-3">
            Complete My Full 2-Minute Audit
          </div>
          <p className="text-slate-400 text-sm mb-5 leading-relaxed">
            Get your personalized{" "}
            <span className="text-gold">Grid Readiness Score</span> plus your
            free <span className="text-gold">2026 Growth Roadmap</span> e-book.
          </p>
          <div className="inline-flex items-center gap-2 text-sm font-display text-gold group-hover:translate-x-0.5 transition-transform">
            Continue Audit <ArrowRight size={14} />
          </div>
        </button>

        {/* Button B — Fast-Track */}
        <button
          type="button"
          onClick={onFastTrack}
          data-testid="speed-gate-fasttrack"
          className="group text-left rounded-sm border border-white/15 p-6 bg-gradient-to-br from-[#0A1122] to-[#0F1830] hover:border-emerald-400/60 transition-all"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-emerald-400 mb-3">
            <Rocket size={12} /> Option B · Fast-Track
          </div>
          <div className="font-display text-lg sm:text-xl text-white leading-snug mb-3">
            Skip to My Live Demo Now
          </div>
          <p className="text-slate-400 text-sm mb-5 leading-relaxed">
            Jump straight into the{" "}
            <span className="text-emerald-400">Magic Mirror</span> — experience
            your business on the Grid. Baseline utility score applies.
          </p>
          <div className="inline-flex items-center gap-2 text-sm font-display text-emerald-400 group-hover:translate-x-0.5 transition-transform">
            Experience the Grid <Sparkles size={14} />
          </div>
        </button>
      </div>

      <div className="mt-6 text-center text-[10px] text-slate-500 font-mono-aviation">
        Your contact was saved · John &amp; Parker are already on it.
      </div>
    </div>
  );
}

function ProgressBar({ current, total }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="wizard-progress">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="flex-1 h-0.5 rounded-sm transition-colors"
          style={{ backgroundColor: i <= current ? "#D4AF37" : "rgba(255,255,255,0.08)" }}
        />
      ))}
    </div>
  );
}

function AlfredBubble({ label, children }) {
  return (
    <div className="flex gap-3 fade-up">
      <div className="w-10 h-10 rounded-sm border border-gold/50 flex items-center justify-center bg-[#0A1122] shrink-0">
        <span className="font-display text-gold text-base">A</span>
      </div>
      <div className="flex-1">
        <div className="hud-label text-[9px] mb-1">{label}</div>
        <div className="text-lg sm:text-xl font-display text-white font-light leading-snug">
          {children}
        </div>
      </div>
    </div>
  );
}

function CityStep({ value, onChange }) {
  return (
    <input
      type="text"
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid="wizard-city-input"
      placeholder="Marion, Delaware, Mansfield, Bucyrus..."
      className="w-full bg-[#0A1122] border border-white/10 rounded-sm px-5 py-4 text-white text-lg font-display focus:outline-none focus:border-gold transition-colors"
    />
  );
}

function SnapshotStep({ value = [], onChange }) {
  const toggle = (id) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };
  return (
    <div className="space-y-2" data-testid="snapshot-step-fields">
      <div className="text-[11px] text-slate-500 font-mono-aviation mb-2 uppercase tracking-[0.22em]">
        Alfred uses these signals to score your Grid Readiness.
      </div>
      {SNAPSHOT_OPTIONS.map((o) => {
        const active = value.includes(o.id);
        const isPos = o.polarity === "pos";
        const activeColor = isPos ? "#34D399" : "#F5C842";
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => toggle(o.id)}
            data-testid={`wizard-snapshot-${o.id}`}
            className={`w-full text-left rounded-sm border px-4 py-3 flex items-center gap-3 transition-all ${
              active
                ? "bg-white/[0.04] text-white"
                : "border-white/10 text-slate-300 hover:border-gold/40 hover:bg-white/[0.02]"
            }`}
            style={active ? {
              borderColor: `${activeColor}80`,
              boxShadow: `0 0 20px ${activeColor}22`,
            } : {}}
          >
            <span
              className={`w-4 h-4 rounded-[3px] border flex items-center justify-center shrink-0 transition-all`}
              style={{
                borderColor: active ? activeColor : "rgba(255,255,255,0.25)",
                background: active ? activeColor : "transparent",
              }}
            >
              {active && (
                <CheckCircle2 size={12} strokeWidth={3} style={{ color: "#060B14" }} />
              )}
            </span>
            <span className="flex-1 font-display text-sm sm:text-base">{o.label}</span>
            <span
              className="font-mono-aviation text-[11px] shrink-0"
              style={{ color: active ? activeColor : "rgba(255,255,255,0.3)" }}
            >
              {o.delta > 0 ? `+${o.delta}` : o.delta}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function CardGrid({ value, onChange, options, cols, testIdPrefix }) {
  const gridCls = cols === 2 ? "grid-cols-1 sm:grid-cols-2" : cols === 4 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3";
  return (
    <div className={`grid ${gridCls} gap-3`}>
      {options.map((o) => {
        const active = value === o.id;
        const Icon = o.icon;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={`wizard-${testIdPrefix}-${o.id.toLowerCase().replace(/\s+/g, "-")}`}
            className={`p-4 rounded-sm border text-left transition-all ${
              active
                ? "border-gold bg-gold/10 text-white"
                : "border-white/10 hover:border-gold/50 text-slate-300 hover:bg-white/5"
            }`}
            style={active ? { boxShadow: "0 0 24px rgba(212,175,55,0.25)" } : {}}
          >
            <div className="flex items-center gap-3">
              {Icon && <Icon size={18} className={active ? "text-gold" : "text-slate-500"} />}
              <span className="font-display text-base">{o.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function EvaluatingPanel() {
  const [phase, setPhase] = useState(0);
  const messages = [
    "Cross-referencing market signals...",
    "Scanning reputation leaks...",
    "Calculating revenue exposure...",
  ];
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 700);
    const t2 = setTimeout(() => setPhase(2), 1400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <div className="mt-16 text-center fade-up" data-testid="wizard-evaluating">
      <div className="relative inline-block">
        <div className="absolute inset-0 rounded-full bg-gold/30 blur-2xl animate-pulse" />
        <div className="relative w-20 h-20 rounded-full border-2 border-gold/40 border-t-gold animate-spin mx-auto" />
      </div>
      <div className="mt-8 hud-label text-gold">Architect Evaluating</div>
      <div className="mt-3 font-display text-2xl text-white font-light min-h-[2rem] transition-all">
        {messages[phase]}
      </div>
    </div>
  );
}

function ResultPanel({ result, answers }) {
  return (
    <div className="mt-12 fade-up" data-testid="wizard-result">
      <div className="flex items-center gap-3 mb-6 justify-center">
        <CheckCircle2 size={20} className="text-[#34D399]" />
        <span className="hud-label text-[#34D399]">Audit Complete</span>
      </div>

      {/* Leak score gauge */}
      <div
        className="relative mx-auto rounded-sm border border-gold/40 p-8 sm:p-10 text-center overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(212,175,55,0.06), rgba(244,63,94,0.04))",
          boxShadow: "0 0 60px rgba(212,175,55,0.2)",
        }}
      >
        <div className="hud-label mb-3">Estimated Weekly Revenue Leak</div>
        <div
          className="font-display text-5xl sm:text-7xl text-white font-light"
          data-testid="leak-dollars"
        >
          $<span className="text-gold">{result.leak_dollars.toLocaleString()}</span>
          <span className="text-slate-500 text-2xl sm:text-3xl ml-2">/ week</span>
        </div>
        <div className="mt-6 flex items-center justify-center gap-3">
          <div className="hud-label text-[10px]">Leak Score</div>
          <div className="w-40 h-1.5 bg-white/10 rounded-sm overflow-hidden">
            <div
              className="h-full"
              style={{
                width: `${result.score}%`,
                background: "linear-gradient(90deg, #34D399, #FBBF24, #F43F5E)",
              }}
            />
          </div>
          <div className="font-mono-aviation text-gold text-sm" data-testid="leak-score">
            {result.score}/100
          </div>
        </div>
      </div>

      {/* Verification card */}
      <div
        className="mt-6 glass-strong rounded-sm p-6 sm:p-8"
        data-testid="verification-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-sm border border-gold/40 flex items-center justify-center shrink-0">
            <ShieldCheck size={20} className="text-gold" />
          </div>
          <div className="flex-1">
            <div className="hud-label mb-1">Verification · Hand-off</div>
            <p
              className="font-display text-lg text-white leading-snug"
              data-testid="verification-message"
            >
              I&apos;ve logged your contact info as{" "}
              <span className="text-gold">{answers.contact || "the address you provided"}</span>.
              I&apos;ve sent your audit for{" "}
              <span className="text-gold">{answers.business_name || "your business"}</span> to{" "}
              <span className="text-gold">John</span> and{" "}
              <span className="text-gold">Parker</span>. They will contact you within{" "}
              <span className="text-gold">24 hours</span>.
            </p>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Tag k="City" v={answers.city} />
              <Tag k="Industry" v={answers.industry} />
              <Tag k="Missed/wk" v={answers.missed_calls} />
              <Tag k="Reviews" v={answers.reviews} />
            </div>
            <div className="mt-5 text-[10px] font-mono-aviation text-slate-500 break-all">
              Ref · {result.lead_id}
            </div>
          </div>
        </div>
      </div>

      {/* Grid Activation Bridge — replaces the "we'll contact you" dead zone */}
      <GridActivationBridge
        niche={answers.industry || "default"}
        businessName={answers.business_name}
        contact={answers.contact}
        leadId={result.lead_id}
        readiness={result.readiness}
      />
    </div>
  );
}

const Tag = ({ k, v }) => (
  <div className="border border-white/10 rounded-sm px-3 py-2">
    <div className="hud-label text-[8px]">{k}</div>
    <div className="text-sm text-white truncate">{v || "—"}</div>
  </div>
);
